import React, { useState, useEffect, useCallback, useRef} from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { SignatureCanvas } from '../components/ui/signature-canvas';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertTriangle,
  FileText,
  Heart,
  PenLine,
  Clock,
  MapPin,
  Loader2,
  ChevronRight,
  Shield,
  XCircle,
  Target,
  Navigation,
  RefreshCw,
} from 'lucide-react';

/**
 * Generate a persistent device ID for SLED compliance
 */
function generateDeviceId() {
  // Check for existing device ID
  let deviceId = localStorage.getItem('bsms_device_id');
  
  if (!deviceId) {
    // Generate new device ID from browser fingerprint + random
    const fingerprint = [
      navigator.userAgent,
      screen.width,
      screen.height,
      screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.language,
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Combine with random UUID component
    const randomPart = Math.random().toString(36).substring(2, 10);
    deviceId = `DEV-${Math.abs(hash).toString(16)}-${randomPart}`;
    
    localStorage.setItem('bsms_device_id', deviceId);
  }
  
  return deviceId;
}

/**
 * SLED Workflow Steps
 */
const WORKFLOW_STEPS = [
  { id: 'accept', label: 'Accept Shift', icon: CheckCircle2 },
  { id: 'sop', label: 'SOP Acknowledgement', icon: FileText },
  { id: 'fit_for_work', label: 'Fit For Work', icon: Heart },
  { id: 'geofence', label: 'Location Check', icon: Target },
  { id: 'sign_on', label: 'Sign On', icon: PenLine },
  { id: 'start_shift', label: 'Start Shift', icon: Clock },
];

/**
 * ShiftWorkflowModal - SLED Compliant Guard Sign-On Flow
 * 
 * Implements the mandatory workflow:
 * 1. Accept Shift
 * 2. SOP Acknowledgement
 * 3. Fit For Work Declaration
 * 4. Digital Signature (Sign-On)
 * 5. Start Shift
 */
export function ShiftWorkflowModal({
  isOpen,
  onClose,
  shift,
  api,
  onWorkflowComplete,
  initialStep = null,
}) {
  const [currentStep, _setCurrentStep] = useState(initialStep || 'accept');

  // RAPID X V6.4 — MONOTONIC WORKFLOW
  //
  // While a workflow modal remains open, completed controls must never be
  // replayed because of dashboard polling, a parent rerender, stale shift
  // props or a delayed SOP response.
  const workflowStepOrder = [
    'accept',
    'sop',
    'fit_for_work',
    'geofence',
    'sign_on',
    'start_shift',
  ];

  const currentStepRef = useRef(initialStep || 'accept');
  const modalShiftSessionRef = useRef(null);

  const setWorkflowStep = useCallback(
    (nextStep, options = {}) => {
      const { force = false } = options;

      if (!workflowStepOrder.includes(nextStep)) {
        console.error(
          '[Rapid X V6.4] Invalid workflow step:',
          nextStep
        );
        return;
      }

      const currentIndex = workflowStepOrder.indexOf(
        currentStepRef.current
      );
      const nextIndex = workflowStepOrder.indexOf(nextStep);

      if (!force && nextIndex < currentIndex) {
        console.warn(
          '[Rapid X V6.4] Prevented workflow regression',
          {
            currentStep: currentStepRef.current,
            attemptedStep: nextStep,
            shiftId: shift?.id || shift?._id,
          }
        );
        return;
      }

      currentStepRef.current = nextStep;
      _setCurrentStep(nextStep);
    },
    [shift?.id, shift?._id]
  );
  const [loading, setLoading] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [notes, setNotes] = useState('');
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [geofenceChecking, setGeofenceChecking] = useState(false);

  // RAPID X V6.4.1 — SAFE FRONTEND ERROR MESSAGE
  const getWorkflowErrorMessage = useCallback(
    (error, fallback) => {
      const detail = error?.response?.data?.detail;

      if (typeof detail === 'string') {
        return detail;
      }

      if (Array.isArray(detail)) {
        return detail
          .map((item) => {
            if (typeof item === 'string') return item;

            const location = Array.isArray(item?.loc)
              ? item.loc.join('.')
              : '';

            const message =
              item?.msg ||
              item?.message ||
              'Validation error';

            return location
              ? `${location}: ${message}`
              : message;
          })
          .join('; ');
      }

      if (detail && typeof detail === 'object') {
        return (
          detail.message ||
          detail.msg ||
          fallback
        );
      }

      return error?.message || fallback;
    },
    []
  );

  // RAPID X V6 — INTELLIGENT SHIFT WORKFLOW
  const [sopRequirementChecking, setSopRequirementChecking] = useState(false);

  const stageRank = useCallback((stage) => {
    const stages = [
      'pending_acceptance',
      'scheduled',
      'accepted',
      'sop_acknowledged',
      'fit_for_work',
      'geofence_validated',
      'signed_on',
      'started',
      'patrol_plan_loaded',
      'patrol_active',
      'patrol_interrupted',
      'signed_off',
      'completed',
    ];

    const index = stages.indexOf(stage);
    return index === -1 ? -1 : index;
  }, []);

  const stageReached = useCallback(
    (currentStage, requiredStage) => {
      const current = stageRank(currentStage);
      const required = stageRank(requiredStage);

      return current >= required && required >= 0;
    },
    [stageRank]
  );

  const shiftHasFitnessDeclaration = useCallback(
    (candidateShift) => {
      if (!candidateShift) return false;

      return (
        candidateShift.fit_for_work === true ||
        Boolean(candidateShift.fit_for_work_at) ||
        stageReached(
          candidateShift.workflow_stage,
          'fit_for_work'
        )
      );
    },
    [stageReached]
  );

  const checkSopRequirement = useCallback(
    async (candidateShift = shift) => {
      const shiftId = candidateShift?.id || candidateShift?._id;

      if (!shiftId) {
        setWorkflowStep('sop');
        return true;
      }

      setSopRequirementChecking(true);

      try {
        const response = await api.get(
          `/shifts/${shiftId}/sop-requirement`
        );

        const required =
          response.data?.requires_sop_acknowledgement !== false;

        if (!required) {
          const fitnessCompleted =
            shiftHasFitnessDeclaration(candidateShift);

          setWorkflowStep(
            fitnessCompleted ? 'geofence' : 'fit_for_work'
          );
        }

        return required;
      } catch (error) {
        // Compliance-safe fallback. Do not silently bypass SOP when the
        // server cannot verify the reusable acknowledgement.
        console.error(
          '[Rapid X V6] SOP requirement check failed:',
          error
        );

        setWorkflowStep('sop');
        return true;
      } finally {
        setSopRequirementChecking(false);
      }
    },
    [api, shift, shiftHasFitnessDeclaration]
  );

  const resolveNextIncompleteStep = useCallback(
    async (candidateShift) => {
      if (!candidateShift) return;

      const stage =
        candidateShift.workflow_stage ||
        'pending_acceptance';

      const isWorkflowComplete =
        stageReached(stage, 'started') ||
        candidateShift.started === true ||
        Boolean(candidateShift.actual_start) ||
        Boolean(candidateShift.actual_start_time);

      if (isWorkflowComplete) {
        onClose();
        onWorkflowComplete?.(candidateShift);
        return;
      }

      const accepted =
        stageReached(stage, 'accepted') ||
        candidateShift.acceptance_status === 'accepted' ||
        Boolean(candidateShift.accepted_at);

      if (!accepted) {
        setWorkflowStep('accept');
        return;
      }

      let sopCompleted =
        candidateShift.sop_acknowledged === true ||
        Boolean(candidateShift.sop_acknowledged_at) ||
        stageReached(stage, 'sop_acknowledged');

      if (!sopCompleted) {
        const sopRequired =
          await checkSopRequirement(candidateShift);

        if (sopRequired) {
          setWorkflowStep('sop');
          return;
        }

        sopCompleted = true;
      }

      const fitnessCompleted =
        shiftHasFitnessDeclaration(candidateShift);

      if (!fitnessCompleted) {
        setWorkflowStep('fit_for_work');
        return;
      }

      const geofenceCompleted =
        candidateShift.geofence_validated === true ||
        Boolean(candidateShift.geofence_validated_at) ||
        stageReached(stage, 'geofence_validated');

      if (!geofenceCompleted) {
        setWorkflowStep('geofence');
        return;
      }

      const signOnCompleted =
        candidateShift.signed_on === true ||
        Boolean(candidateShift.signed_on_at) ||
        Boolean(candidateShift.sign_on_at) ||
        Boolean(candidateShift.signature_path) ||
        stageReached(stage, 'signed_on');

      if (!signOnCompleted) {
        setWorkflowStep('sign_on');
        return;
      }

      setWorkflowStep('start_shift');
    },
    [
      checkSopRequirement,
      onClose,
      onWorkflowComplete,
      shiftHasFitnessDeclaration,
      stageReached,
    ]
  );

  // RAPID X V6.4 — INITIALISE ONCE PER MODAL SESSION
  //
  // Dashboard polling and parent rerenders must not restart the workflow.
  // Initialise only when the modal opens for a different shift.
  useEffect(() => {
    if (!isOpen || !shift) {
      modalShiftSessionRef.current = null;
      return;
    }

    const shiftId = String(shift.id || shift._id || '');

    if (!shiftId) {
      console.error(
        '[Rapid X V6.4] Workflow shift has no ID',
        shift
      );
      return;
    }

    if (modalShiftSessionRef.current === shiftId) {
      return;
    }

    modalShiftSessionRef.current = shiftId;

    if (initialStep) {
      setWorkflowStep(initialStep, { force: true });
      return;
    }

    // RAPID X V6.4.1 — do not temporarily reset to Accept.
    //
    // The dashboard opens this modal to resume an already accepted shift.
    // Forcing Accept before the async resolver completes creates a race in
    // which the officer can accidentally submit acceptance a second time.
    resolveNextIncompleteStep(shift);
  }, [
    initialStep,
    isOpen,
    resolveNextIncompleteStep,
    setWorkflowStep,
    shift,
  ]);

  // Request GPS location
  const requestGPS = useCallback(() => {
    setGpsLoading(true);
    setGpsError(null);
    
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      setGpsLoading(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setGpsLoading(false);
      },
      (error) => {
        setGpsError(`GPS Error: ${error.message}`);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, []);
  
  // Auto-request GPS when reaching geofence, sign-on or start-shift step
  useEffect(() => {
    if ((currentStep === 'geofence' || currentStep === 'sign_on' || currentStep === 'start_shift') && !gpsLocation) {
      requestGPS();
    }
  }, [currentStep, gpsLocation, requestGPS]);
  
  // Handle Accept Shift
  const handleAcceptShift = async () => {
    const shiftId = shift?.id || shift?._id;

    if (!shiftId) {
      toast.error('Shift ID is missing');
      return;
    }

    const originalStage =
      shift?.workflow_stage ||
      'pending_acceptance';

    const alreadyAccepted =
      shift?.acceptance_status === 'accepted' ||
      Boolean(shift?.accepted_at) ||
      ![
        'pending_acceptance',
        'scheduled',
        '',
        null,
        undefined,
      ].includes(originalStage);

    setLoading(true);

    let acceptedShift = {
      ...shift,
      id: shiftId,
      acceptance_status: 'accepted',
      accepted_at:
        shift?.accepted_at ||
        new Date().toISOString(),
      workflow_stage:
        originalStage === 'pending_acceptance' ||
        originalStage === 'scheduled'
          ? 'accepted'
          : originalStage,
    };

    try {
      if (!alreadyAccepted) {
        try {
          await api.post(
            `/shifts/${shiftId}/respond`,
            { response: 'accepted' }
          );

          toast.success('Shift accepted');
        } catch (acceptError) {
          const detail =
            acceptError?.response?.data?.detail;

          const alreadyCommitted =
            typeof detail === 'string' &&
            (
              detail.toLowerCase().includes(
                'already accepted'
              ) ||
              detail.toLowerCase().includes(
                'current stage'
              )
            );

          if (!alreadyCommitted) {
            console.error(
              '[Rapid X V6.4.5B] Acceptance failed',
              acceptError
            );

            toast.error(
              getWorkflowErrorMessage(
                acceptError,
                'Failed to accept shift'
              )
            );

            return;
          }
        }
      }

      // Acceptance is now committed. Continuation is a separate operation
      // and must never produce a false "Failed to accept shift" message.
      try {
        await resolveNextIncompleteStep(
          acceptedShift
        );
      } catch (continuationError) {
        console.error(
          '[Rapid X V6.4.5B] Acceptance succeeded but continuation failed',
          continuationError
        );

        setWorkflowStep('sop');

        toast.warning(
          'Shift accepted. Continue the sign-on workflow.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle SOP Acknowledgement
  const handleSOPAcknowledge = async (acknowledged) => {
    setLoading(true);
    try {
      const response = await api.post(`/shifts/${shift.id}/acknowledge-sop`, {
        acknowledged,
        notes,
      });
      
      if (response.data.blocked) {
        toast.warning(response.data.message);
        onClose();
      } else {
        try {
          await api.post(
            `/shifts/${shift.id}/record-sop-acknowledgement`
          );
        } catch (recordError) {
          console.error(
            'Reusable SOP acknowledgement could not be recorded:',
            recordError
          );
          toast.warning(
            'SOP accepted for this shift, but the renewal record could not be saved'
          );
        }

        toast.success('SOP acknowledgement recorded');
        setNotes('');

        setWorkflowStep(
          shiftHasFitnessDeclaration(shift)
            ? 'geofence'
            : 'fit_for_work'
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record SOP acknowledgement');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Fit For Work Declaration
  const handleFitForWork = async () => {
    setLoading(true);
    try {
      await api.post(`/shifts/${shift.id}/fit-for-work`, {
        confirmed: true,
        notes,
      });
      toast.success('Fit for work declaration recorded');
      setNotes('');
      setWorkflowStep('geofence');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record declaration');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Geofence Validation
  const handleGeofenceValidation = async () => {
    if (!gpsLocation) {
      toast.error('GPS location is required. Please enable location services.');
      return;
    }
    
    setGeofenceChecking(true);
    setGeofenceStatus(null);
    
    try {
      const response = await api.post(`/shifts/${shift.id}/validate-geofence`, {
        latitude: gpsLocation.latitude,
        longitude: gpsLocation.longitude,
        accuracy: gpsLocation.accuracy || 50,
        device_id: generateDeviceId(),
      });
      
      setGeofenceStatus(response.data);

      // P1 STATIC SHIFT FIX:
      // Backend static-shift bypass may return success using a different shape
      // than the old mobile geofence response. Treat any explicit successful
      // bypass / allowed / within_geofence response as pass and continue.
      const geofencePassed =
        response.data?.status === 'pass' ||
        response.data?.status === 'bypassed' ||
        response.data?.within_geofence === true ||
        response.data?.allowed === true ||
        response.data?.bypass === true ||
        response.data?.static_shift === true;

      if (geofencePassed) {
        toast.success(response.data?.message || 'Location verified');
        setWorkflowStep('sign_on');
      } else if (response.data.status === 'warning') {
        toast.warning(response.data.message);
        // Allow proceed with warning
        setWorkflowStep('sign_on');
      } else {
        toast.error(response.data.message || 'Location validation failed');
        // Stay on geofence step - user needs to move or retry
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to validate location';
      setGeofenceStatus({
        status: 'fail',
        message: detail,
        within_geofence: false,
      });
      toast.error(detail);
    } finally {
      setGeofenceChecking(false);
    }
  };
  
  // Recheck GPS location
  const handleRecheckLocation = () => {
    setGpsLocation(null);
    setGeofenceStatus(null);
    requestGPS();
  };
  
  // Handle Sign-On
  const handleSignOn = async () => {

    if (!signatureData) {
      toast.error('Please provide your signature');
      return;
    }
    
    if (!gpsLocation) {
      toast.error('GPS location is required. Please enable location services.');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/shifts/${shift.id}/sign-on`, {
        signature_data: signatureData,
        latitude: gpsLocation.latitude,
        longitude: gpsLocation.longitude,
        device_id: generateDeviceId(),
        notes,
      });
      toast.success('Sign-on recorded');
      setNotes('');
      setSignatureData(null);
      setWorkflowStep('start_shift');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record sign-on');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Start Shift
  const handleStartShift = async (earlyStart = false) => {
    if (!gpsLocation) {
      toast.error('GPS location is required. Please enable location services.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/shifts/${shift.id}/start-shift`, {
        latitude: gpsLocation.latitude,
        longitude: gpsLocation.longitude,
        early_start: earlyStart,
        authorization_note: earlyStart ? notes : null,
      });
      
      toast.success('Shift started successfully!');
      
      // Check if patrol route is available
      if (response.data.patrol_route_available) {
        toast.info(`Patrol route "${response.data.patrol_route.name}" is now available`);
      }
      
      onClose();
      onWorkflowComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start shift');
    } finally {
      setLoading(false);
    }
  };
  
  // Get current step index
  const currentStepIndex = WORKFLOW_STEPS.findIndex(s => s.id === currentStep);
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'accept':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Unconfirmed Shift</h3>
                <p className="text-zinc-400 mt-2">
                  You have been assigned to this shift. Please accept or decline.
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-[#09090b] rounded-lg border border-[#3f3f46] space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span className="text-white font-medium">{shift?.site_name}</span>
                <Badge variant="outline" className="ml-auto">{shift?.site_code}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-400" />
                <span className="text-zinc-300">
                  {new Date(shift?.start_time).toLocaleDateString('en-AU', { 
                    weekday: 'short', day: 'numeric', month: 'short' 
                  })}
                  {' '}
                  {new Date(shift?.start_time).toLocaleTimeString('en-AU', { 
                    hour: '2-digit', minute: '2-digit' 
                  })}
                  {' - '}
                  {new Date(shift?.end_time).toLocaleTimeString('en-AU', { 
                    hour: '2-digit', minute: '2-digit' 
                  })}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                className="h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg"
                onClick={handleAcceptShift}
                disabled={loading || sopRequirementChecking}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                Accept Shift
              </Button>
              <Button
                variant="outline"
                className="h-14 border-red-500/50 text-red-400 hover:bg-red-500/10 font-bold text-lg"
                onClick={() => {
                  onClose();
                  toast.info('Shift declined');
                }}
                disabled={loading || sopRequirementChecking}
              >
                <XCircle className="w-5 h-5 mr-2" />
                Decline
              </Button>
            </div>
          </div>
        );
        
      case 'sop':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">SOP Acknowledgement</h3>
                <p className="text-zinc-400 mt-2">
                  Standard Operating Procedures for <span className="text-white font-medium">{shift?.site_name}</span>
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-center font-medium">
                Have you read and do you understand the SOPs for this site?
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                className="h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg"
                onClick={() => handleSOPAcknowledge(true)}
                disabled={loading || sopRequirementChecking}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                Yes
              </Button>
              <Button
                variant="outline"
                className="h-14 border-red-500/50 text-red-400 hover:bg-red-500/10 font-bold text-lg"
                onClick={() => handleSOPAcknowledge(false)}
                disabled={loading || sopRequirementChecking}
              >
                <XCircle className="w-5 h-5 mr-2" />
                No
              </Button>
            </div>
            
            <p className="text-xs text-zinc-500 text-center">
              If you select "No", you will need to contact your supervisor before commencing duty.
            </p>
          </div>
        );
        
      case 'fit_for_work':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Fit For Work Declaration</h3>
                <p className="text-zinc-400 mt-2">
                  Please confirm you are fit for duty
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-[#09090b] border border-[#3f3f46] rounded-lg space-y-4">
              <p className="text-zinc-300 text-center">
                I declare that I am fit for work and able to perform my duties safely and effectively.
              </p>
              <p className="text-xs text-zinc-500 text-center">
                This includes being free from the effects of alcohol, drugs, or any condition that may impair my ability to work safely.
              </p>
            </div>
            
            <Button
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg"
              onClick={handleFitForWork}
              disabled={loading || sopRequirementChecking}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-5 h-5 mr-2" />
              )}
              I Understand and Am Fit For Work
            </Button>
          </div>
        );
        
      case 'geofence':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                geofenceStatus?.status === 'pass' ? 'bg-emerald-500/20' :
                geofenceStatus?.status === 'fail' ? 'bg-red-500/20' :
                geofenceStatus?.status === 'warning' ? 'bg-amber-500/20' :
                'bg-amber-500/20'
              }`}>
                <Target className={`w-8 h-8 ${
                  geofenceStatus?.status === 'pass' ? 'text-emerald-400' :
                  geofenceStatus?.status === 'fail' ? 'text-red-400' :
                  geofenceStatus?.status === 'warning' ? 'text-amber-400' :
                  'text-amber-400'
                }`} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Location Verification</h3>
                <p className="text-zinc-400 mt-2">
                  Confirm you are at <span className="text-white font-medium">{shift?.site_name}</span>
                </p>
              </div>
            </div>
            
            {/* GPS Location Status */}
            <div className="p-4 bg-[#09090b] rounded-lg border border-[#3f3f46] space-y-3">
              <p className="text-xs text-zinc-500 uppercase tracking-wide">Current Location</p>
              
              {gpsLoading ? (
                <div className="flex items-center gap-3 text-zinc-400">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  <div>
                    <p className="text-white">Acquiring GPS location...</p>
                    <p className="text-xs text-zinc-500">This may take a moment</p>
                  </div>
                </div>
              ) : gpsError ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <div>
                      <p className="text-red-400 font-medium">GPS Unavailable</p>
                      <p className="text-xs text-zinc-500">{gpsError}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">
                    <p className="font-medium">Enable Location Access</p>
                    <p className="mt-1 text-red-400/80">
                      Go to your browser settings and allow location access for this site.
                    </p>
                  </div>
                </div>
              ) : gpsLocation ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Navigation className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-white">Location Acquired</p>
                      <p className="text-xs text-zinc-500 font-mono">
                        {gpsLocation.latitude.toFixed(5)}, {gpsLocation.longitude.toFixed(5)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">GPS Accuracy:</span>
                    <Badge className={`${
                      gpsLocation.accuracy <= 50 ? 'bg-emerald-500/20 text-emerald-400' :
                      gpsLocation.accuracy <= 100 ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      ±{Math.round(gpsLocation.accuracy)}m
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-zinc-400">
                  <MapPin className="w-5 h-5" />
                  <p>Waiting for location...</p>
                </div>
              )}
            </div>
            
            {/* Geofence Status */}
            {geofenceStatus && (
              <div className={`p-4 rounded-lg border ${
                geofenceStatus.status === 'pass' ? 'bg-emerald-500/10 border-emerald-500/30' :
                geofenceStatus.status === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                'bg-red-500/10 border-red-500/30'
              }`}>
                <div className="flex items-start gap-3">
                  {geofenceStatus.status === 'pass' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  ) : geofenceStatus.status === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-2">
                    <p className={`font-medium ${
                      geofenceStatus.status === 'pass' ? 'text-emerald-400' :
                      geofenceStatus.status === 'warning' ? 'text-amber-400' :
                      'text-red-400'
                    }`}>
                      {geofenceStatus.status === 'pass' ? 'Within Site Boundary' :
                       geofenceStatus.status === 'warning' ? 'Proceed With Caution' :
                       'Outside Site Boundary'}
                    </p>
                    <p className="text-sm text-zinc-400">{geofenceStatus.message}</p>
                    
                    {/* Distance info */}
                    {geofenceStatus.distance_m !== undefined && (
                      <div className="flex items-center gap-4 text-xs mt-2">
                        <span className="text-zinc-500">
                          Distance: <span className="text-white font-mono">{geofenceStatus.distance_m}m</span>
                        </span>
                        <span className="text-zinc-500">
                          Allowed: <span className="text-white font-mono">{geofenceStatus.radius_m}m</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Recheck location button */}
              <Button
                variant="outline"
                className="w-full border-[#3f3f46] text-zinc-300"
                onClick={handleRecheckLocation}
                disabled={gpsLoading || geofenceChecking}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${gpsLoading ? 'animate-spin' : ''}`} />
                Recheck Location
              </Button>
              
              {/* Validate geofence button */}
              <Button
                className={`w-full h-14 font-bold text-lg ${
                  geofenceStatus?.status === 'fail' 
                    ? 'bg-zinc-700 hover:bg-zinc-600' 
                    : 'bg-amber-500 hover:bg-amber-600'
                }`}
                onClick={handleGeofenceValidation}
                disabled={!gpsLocation || geofenceChecking || gpsLoading}
              >
                {geofenceChecking ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Target className="w-5 h-5 mr-2" />
                )}
                {geofenceStatus?.status === 'fail' ? 'Retry Verification' : 'Verify Location'}
              </Button>
            </div>
            
            {/* Info message */}
            <p className="text-xs text-zinc-500 text-center">
              You must be within the site geofence boundary to sign on. If you're having location issues, try moving to an open area.
            </p>
          </div>
        );
        
      case 'sign_on':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center">
                <PenLine className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Signature Required</h3>
                <p className="text-zinc-400 mt-2">
                  Please sign on the line to confirm your attendance
                </p>
              </div>
            </div>
            
            <SignatureCanvas
              onSignatureChange={setSignatureData}
              width={360}
              height={180}
            />
            
            {/* GPS Status */}
            <div className="p-3 bg-[#09090b] rounded-lg border border-[#3f3f46]">
              {gpsLoading ? (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Acquiring GPS location...</span>
                </div>
              ) : gpsLocation ? (
                <div className="flex items-center gap-2 text-emerald-400">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">
                    Location captured (±{Math.round(gpsLocation.accuracy)}m)
                  </span>
                </div>
              ) : gpsError ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">{gpsError}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={requestGPS}>
                    Retry
                  </Button>
                </div>
              ) : null}
            </div>
            
            <Button
              className="w-full h-14 bg-purple-500 hover:bg-purple-600 text-white font-bold text-lg"
              onClick={handleSignOn}
              disabled={loading || !signatureData || !gpsLocation}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <PenLine className="w-5 h-5 mr-2" />
              )}
              Confirm Sign-On
            </Button>
          </div>
        );
        
      case 'start_shift':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-cyan-500/20 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Start Your Shift</h3>
                <p className="text-zinc-400 mt-2">
                  Sign-on complete. You are ready to start.
                </p>
              </div>
            </div>
            
            {/* GPS Status */}
            <div className="p-3 bg-[#09090b] rounded-lg border border-[#3f3f46]">
              {gpsLoading ? (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Acquiring GPS location...</span>
                </div>
              ) : gpsLocation ? (
                <div className="flex items-center gap-2 text-emerald-400">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">
                    Location captured (±{Math.round(gpsLocation.accuracy)}m)
                  </span>
                </div>
              ) : gpsError ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">{gpsError}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={requestGPS}>
                    Retry
                  </Button>
                </div>
              ) : null}
            </div>
            
            <div className="space-y-3">
              <Button
                className="w-full h-16 bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-xl"
                onClick={() => handleStartShift(false)}
                disabled={loading || !gpsLocation}
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                ) : (
                  <Clock className="w-6 h-6 mr-2" />
                )}
                LOG MY ROSTERED START
              </Button>
              
              <Button
                variant="outline"
                className="w-full h-12 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                onClick={() => handleStartShift(true)}
                disabled={loading || !gpsLocation}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                STARTING EARLY WITH AUTHORISATION
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Shift Sign-On
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Complete all steps to start your shift
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between px-2 py-4">
          {WORKFLOW_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted 
                      ? 'bg-emerald-500 text-white' 
                      : isCurrent 
                        ? 'bg-cyan-500 text-white' 
                        : 'bg-[#3f3f46] text-zinc-500'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className={`text-[10px] ${
                    isCurrent ? 'text-cyan-400 font-medium' : 'text-zinc-500'
                  }`}>
                    {step.label.split(' ')[0]}
                  </span>
                </div>
                {index < WORKFLOW_STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${
                    index < currentStepIndex ? 'bg-emerald-500' : 'bg-[#3f3f46]'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShiftWorkflowModal;
